import { CombinedGraphQLErrors } from "@apollo/client";
import { redirect } from "@tanstack/react-router";
import type { ShowMetaTarget } from "@/lib/showMetaTags";
import { apolloClient } from "@/api/apiClient";
import { GET_SERIES_DETAILS } from "@/codegen/graphql/queries/GetSeriesDetails.query";
import { GET_SHOW } from "@/codegen/graphql/queries/GetShows.query";
import { isShowNotFound } from "@/lib/showNotFound";
import { buildSeriesParam, parseSeriesParam } from "@/lib/showSlug";

/**
 * Mobile universal links opened on the web. `/series-details` resolves to the
 * series details page (`target: "series"`, SHO-757); `/series-player` and its
 * legacy spellings to the player. Always throws a redirect: to the show's
 * `/series/{slug}-{id}` or canonical `/{slug}-{id}/0`, or home when the id
 * leads nowhere. See `.claude/rules/mobile-deeplinks.md` for why the show is
 * fetched first.
 */
export const redirectSeriesDeeplink = async (
  search: Record<string, unknown>,
  {
    pathId,
    target = "player",
  }: { pathId?: string; target?: ShowMetaTarget } = {},
): Promise<never> => {
  // Attribution rides on the query string (`source`, `utm_*`, `fbclid`), so
  // everything is forwarded but `id` — to the player, where it is now the
  // path, and to home alike: a dead campaign link is still a campaign visit.
  const { id, ...attribution } = search;

  // `?id=81` reaches us as the number `81`: the router JSON-parses search
  // values. A missing or malformed id goes home without the `GetShow` the API
  // would refuse and Sentry would then report (SHO-707).
  const showId = parseSeriesParam(String(pathId ?? id ?? ""));
  if (!showId) throw redirect({ to: "/", search: attribution });

  // The destination's own query, so its loader is served from the cache
  // instead of fetching the show a second time.
  const show = await apolloClient
    .query({
      query: target === "series" ? GET_SERIES_DETAILS : GET_SHOW,
      variables: { showId },
    })
    .then(
      ({ data }) => data?.show,
      (error: unknown) => {
        // Only the API refusing the id (`Show not found`, the same test as
        // the player's loader) means it leads nowhere. Anything else is
        // rethrown, so the deeplink stays in the address bar: a dead token
        // belongs to the 401 recovery, which reloads this very URL, and an
        // outage (offline, a 5xx, a resolver failure) to the error screen's
        // Retry rather than a home that hides it.
        const isUnknownShow =
          CombinedGraphQLErrors.is(error) && error.errors.some(isShowNotFound);
        if (!isUnknownShow) throw error;
        return undefined;
      },
    );
  if (!show) throw redirect({ to: "/", search: attribution });

  const series = buildSeriesParam(show.title, show.id);
  if (target === "series")
    throw redirect({
      to: "/series/$series",
      params: { series },
      search: attribution,
      // Read by the page's `open series details`: the deeplink is its origin.
      state: { seriesDetailsOrigin: "series details deeplink" },
    });
  throw redirect({
    to: "/$series/$episode",
    params: { series, episode: "0" },
    search: attribution,
  });
};
